import { useIntl, useSelector } from 'umi';
import { F } from '@/utils/intl';
import React, { useEffect, useMemo, useState } from 'react';
import { Empty, Segmented, Space, Tooltip } from 'antd';
import { CalendarOutlined, EditOutlined, GlobalOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';
import HourSelector from '@/components/Filters/HourSelector';

import { Views } from 'conversional-big-calendar';
import { TimeSlot } from 'conversional-journey';

import { getCurrentDaySlots, shouldDisableDate } from '@/components/AppointmentBooking/TimeslotSelector/utils';
import TimeSlotHourList from '@/components/AppointmentBooking/TimeslotSelector/TimeSlotHourList';
import InlineDatePicker from '@/components/Kanban/ServiceRequests/Calendar/InlineDatePicker';
import { FormatDate } from '@/components/PrettyDate';
import TimeZoneSelector from '@/components/TimeZoneSelector';
import * as PreferencesSelectors from '@/selectors/preferencesSelectors';
import styles from './index.less';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

export default function AppointmentBookingTimeSelector({ selectedSlot, timeslots = [], showPlaceHolder = false, flexibleTime = false, onSelect, date, onDateChange, extra, recurring = false, showTimezone = false }: {
  timeslots: TimeSlot[],
  flexibleTime?: boolean,
  showPlaceHolder?: boolean,
  selectedSlot: TimeSlot,
  date: Date,
  onDateChange: (date: Date) => void,
  onSelect: (slot: TimeSlot) => void,
  /** Rendered inside the header, above the selected date/time summary (e.g. the recurrence field). */
  extra?: React.ReactNode,
  /**
   * The event repeats: the single-date box and the inline calendar are hidden —
   * the series' dates come from the rule (described by the recurrence field in
   * `extra`), only the time of day remains editable here.
   */
  recurring?: boolean,
  /**
   * Show a timezone picker in the header. It edits the shared calendar timezone
   * preference (Redux mode), so every time shown here and on the calendar grid
   * re-labels to the chosen zone in lockstep.
   */
  showTimezone?: boolean
}) {
  const intl = useIntl();

  // All wall-clock reads and compositions below happen in the user's selected
  // calendar timezone, matching the calendar grid and the FormatDate header —
  // never in the browser timezone, which may differ.
  const timezone = useSelector(PreferencesSelectors.selectSelectedTimezone);
  const inTz = (value) => dayjs(value).tz(timezone);
  // The selected day as the picker represents it: browser-local midnight of
  // the selected-timezone calendar day (a day label, not an instant).
  const toDayLabelDate = (instant) => dayjs(inTz(instant).format('YYYY-MM-DD')).toDate();
  const composeInTz = (day: Date, hour: number, minute: number) =>
    dayjs.tz(`${dayjs(day).format('YYYY-MM-DD')} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`, timezone);

  // Slot-based flows can switch to free start–end entry on any day (the
  // "Custom time" mode); the flexibleTime flow is free-entry by definition.
  const [freeMode, setFreeMode] = useState(false);
  const flexibleEditing = flexibleTime || freeMode;

  // Fresh availability (service/agent/timezone change) invalidates a custom
  // pick — fall back to the slot list.
  useEffect(() => {
    setFreeMode(false);
  }, [timeslots]);

  useEffect(() => {
    // Re-run on timezone change too: switching zones can move the appointment
    // across midnight, so the highlighted calendar day must follow.
    if (selectedSlot?.start)
      onDateChange(toDayLabelDate(selectedSlot.start))

  }, [selectedSlot, timezone])

  const currentSlots = useMemo(() => {
    return getCurrentDaySlots(date, timeslots, timezone)
  }, [date, timeslots, timezone])
  const handleDateChange = (date) => {
    onDateChange(date)
    if (flexibleEditing && selectedSlot?.start && selectedSlot?.end){
      const startTime = inTz(selectedSlot.start);
      const endTime = inTz(selectedSlot.end);
      onSelect({
        start: composeInTz(date, startTime.hour(), startTime.minute()),
        end: composeInTz(date, endTime.hour(), endTime.minute()),
      })
    }
  }

  // Extra slot fields (resource_id, rawStart…) are carried along untouched so
  // an edited pick stays a valid slot object for the submitting drawer.
  const setStartTime = (startTime: string) => {
    if (startTime && selectedSlot?.end) {
      const [startHour, startMinute] = startTime.split(':').map(Number);
      onSelect({
        ...selectedSlot,
        start: composeInTz(date, startHour, startMinute),
        end: inTz(selectedSlot.end),
      });
    }
  };
  const setEndTime = (endTime: string) => {
    if (endTime && selectedSlot?.start) {
      const [endHour, endMinute] = endTime.split(':').map(Number);
      onSelect({
        ...selectedSlot,
        start: inTz(selectedSlot.start),
        end: composeInTz(date, endHour, endMinute),
      });
    }
  };

  const handleModeChange = (mode) => {
    const custom = mode === 'custom';
    setFreeMode(custom);
    if (custom && (!selectedSlot?.start || !selectedSlot?.end)) {
      onSelect({ start: composeInTz(date, 8, 0), end: composeInTz(date, 9, 0) });
    }
    if (!custom) {
      // Snap back onto a real slot — free-form times are not valid slot picks.
      onSelect(currentSlots[0] ?? null);
    }
  };

  const timeRangeEditor = (
    <div className={`${styles.timeslotSelectorHeader} ${styles.noBorder}`}>
      <div className={styles.timeslotSelectorTitle}><F id='common.date.time' defaultMessage='Time' /></div>
      <div className={styles.flexibleTimeContainer}>
        <HourSelector
          value={selectedSlot?.start ? inTz(selectedSlot.start).format('HH:mm') : null}
          onChange={setStartTime}
          placeholder="Start time"
        />
        <span>-</span>
        <HourSelector
          value={selectedSlot?.end ? inTz(selectedSlot.end).format('HH:mm') : null}
          from={selectedSlot?.start ? inTz(selectedSlot.start).format('HH:mm') : null}
          onChange={setEndTime}
          placeholder="End time"
        />
      </div>
    </div>
  );

  if (timeslots.length === 0 && !showPlaceHolder && !flexibleTime) return <Empty description={intl.formatMessage({ id: 'pages.calendar.noSlotsAvailable', defaultMessage: "No available slots for current date" })} />
  return <div data-testid="timeslot-selector-container" className={styles.timeslotSelectorContainer}>
    <div className={styles.timeslotSelectorHeader}>
      <div className={styles.timeslotSelectorTitle}>
        <F id='pages.calendar.appointmentDateAndTime' defaultMessage='Appointment Date & Time' />
      </div>
      {showTimezone && (
        <div className={styles.timezoneSelectorRow}>
          <TimeZoneSelector
            size='small'
            minWidth={0}
            variant='borderless'
            prefix={<GlobalOutlined />}
            placeholder={intl.formatMessage({
              id: 'pages.locations.selectTimeZonePlaceholder',
              defaultMessage: 'Select timezone',
            })}
          />
        </div>
      )}
      {extra && <div className={styles.headerExtra}>{extra}</div>}
      {!recurring && <div className={styles.selectedTimeslotPresentation}>
        <Space>
          <CalendarOutlined />
          <FormatDate date={selectedSlot?.start} format={'MMMM DD, YYYY'} />
          <span>-</span>
          <FormatDate date={selectedSlot?.start} format={'HH:mm'} />
          <span>-</span>
          <FormatDate date={selectedSlot?.end} format={'HH:mm'} />
        </Space>
      </div>}
      {!flexibleTime && (
        <Segmented
          block
          size={'small'}
          className={styles.modeSegmented}
          data-testid="time-mode-switch"
          value={freeMode ? 'custom' : 'slots'}
          onChange={handleModeChange}
          options={[
            { label: intl.formatMessage({ id: 'pages.calendar.timeEdit.availableSlots', defaultMessage: 'Available slots' }), value: 'slots' },
            { label: intl.formatMessage({ id: 'pages.calendar.timeEdit.customTime', defaultMessage: 'Custom time' }), value: 'custom' },
          ]}
        />
      )}
    </div>
    {!recurring && <div className={styles.inlineDatePickerContainer}>
      <InlineDatePicker view={Views.DAY} disabledDate={date => flexibleEditing ? false : shouldDisableDate(date, timeslots)} value={date}
                        onChange={handleDateChange} />
    </div>}
    {flexibleEditing ?
      <div>{timeRangeEditor}</div>
      : <TimeSlotHourList selectedSlot={selectedSlot}
                          hideDayIfNoSlots
                          onlyShowStart
                          date={date}
                          className={styles.verticalHourList}
                          currentSlots={currentSlots}
                          onSelectSlot={onSelect}
                          selectedExtra={
                            <Tooltip title={intl.formatMessage({ id: 'pages.calendar.timeEdit.customizeTime', defaultMessage: 'Customize meeting time' })}>
                              {/* span, not Button — it lives inside the slot <button>. */}
                              <span
                                data-testid="customize-time-btn"
                                role={'button'}
                                className={styles.customizeTimeButton}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleModeChange('custom');
                                }}
                              >
                                <EditOutlined />
                              </span>
                            </Tooltip>
                          } />}
  </div>
}
